import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface Participant {
  id: string;
  first_name: string;
  last_name: string;
  photo_url?: string;
  phone?: string;
}

interface ChatMessage {
  id: number;
  match_id: number;
  user_id: string;
  message: string;
  created_at: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    photo_url?: string;
  };
}

interface Props {
  matchId: number;
}

const API_URL = process.env.REACT_APP_API_URL ?? '';

function Avatar({ user }: { user?: ChatMessage['user'] }) {
  if (!user) return null;
  const inner = user.photo_url ? (
    <img
      src={`${API_URL}${user.photo_url}`}
      alt={user.first_name}
      className="w-7 h-7 rounded-full object-cover flex-shrink-0"
    />
  ) : (
    <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
      {user.first_name[0]}{user.last_name[0]}
    </div>
  );
  return <Link to={`/users/${user.id}`}>{inner}</Link>;
}

export default function MatchChat({ matchId }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isError } = useQuery<ChatMessage[]>({
    queryKey: ['match-chat', matchId],
    queryFn: () => apiClient.get<ChatMessage[]>(`/api/matches/${matchId}/messages`).then((r) => r.data),
    refetchInterval: 10000,
    retry: false,
  });

  const { data: participants = [] } = useQuery<Participant[]>({
    queryKey: ['match-participants', matchId],
    queryFn: () => apiClient.get<Participant[]>(`/api/matches/${matchId}/participants`).then((r) => r.data),
    retry: false,
  });

  const phoneParticipants = participants.filter((p) => p.phone && p.id !== user?.id);

  const sendMutation = useMutation({
    mutationFn: (message: string) =>
      apiClient.post<ChatMessage>(`/api/matches/${matchId}/messages`, { message }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match-chat', matchId] });
      setText('');
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || sendMutation.isPending) return;
    sendMutation.mutate(text.trim());
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  }

  if (isError) return null;

  return (
    <div className="card">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        Termin vereinbaren
        <span className="text-xs text-gray-400 font-normal ml-1">– nur für Spieler & Admins sichtbar</span>
      </h3>

      {/* Telefonnummern der Gegner (wenn freigegeben) */}
      {phoneParticipants.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4">
          {phoneParticipants.map((p) => (
            <a
              key={p.id}
              href={`tel:${p.phone}`}
              className="flex items-center gap-2 px-3 py-1.5 bg-accent-50 border border-accent-200 rounded-lg text-sm hover:bg-accent-100 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-accent-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="font-medium text-accent-800">{p.first_name} {p.last_name}:</span>
              <span className="text-accent-700">{p.phone}</span>
            </a>
          ))}
        </div>
      )}

      {/* Nachrichten */}
      <div className="space-y-3 max-h-72 overflow-y-auto mb-4 pr-1">
        {messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            Noch keine Nachrichten. Schreib deinem Gegner!
          </p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.user_id === user?.id;
            return (
              <div key={msg.id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                <Avatar user={msg.user} />
                <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                  {!isOwn && msg.user && (
                    <Link
                      to={`/users/${msg.user_id}`}
                      className="text-xs text-gray-400 mb-0.5 ml-1 hover:text-primary-600"
                    >
                      {msg.user.first_name} {msg.user.last_name}
                    </Link>
                  )}
                  <div className={`px-3 py-2 rounded-2xl text-sm leading-snug ${
                    isOwn
                      ? 'bg-primary-600 text-white rounded-tr-sm'
                      : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                  }`}>
                    {msg.message}
                  </div>
                  <span className="text-xs text-gray-300 mt-0.5 mx-1">
                    {new Date(msg.created_at).toLocaleString('de-DE', {
                      day: '2-digit', month: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Eingabe */}
      {user && (
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nachricht schreiben… (Enter zum Senden)"
            rows={2}
            className="input flex-1 resize-none text-sm"
          />
          <button
            type="submit"
            disabled={!text.trim() || sendMutation.isPending}
            className="btn btn-primary p-2.5 self-end"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </form>
      )}
    </div>
  );
}
